/**
 * @module utils/adminAnalytics
 * @description Pure helpers for the admin overview: questionnaire composite
 * scoring (SUS, trust), aggregation, and CSV/JSON export. No React, no I/O.
 */

// The five study conditions, in presentation order.
export const CONDITIONS = [
  "NO EXPLANATION",
  "SIMPLE VISUAL",
  "Interactive Textual XAI method",
  "Interactive Visual XAI method",
  "Interactive Complementary XAI method",
];

// Short labels for compact tables/charts.
export const CONDITION_SHORT = {
  "NO EXPLANATION": "No explanation",
  "SIMPLE VISUAL": "Simple visual",
  "Interactive Textual XAI method": "Interactive textual",
  "Interactive Visual XAI method": "Interactive visual",
  "Interactive Complementary XAI method": "Complementary",
};

export function conditionLabel(mode) {
  if (!mode) return "(no condition)";
  return CONDITION_SHORT[mode] || mode;
}

// ---- numeric helpers ----

export function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function mean(values) {
  const nums = values.filter((v) => v !== null && v !== undefined && Number.isFinite(v));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function round(value, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const f = Math.pow(10, digits);
  return Math.round(value * f) / f;
}

// ---- SUS (System Usability Scale), 10 items, 1..5 ----
// Standard scoring: positive items score (v-1), negative items score (5-v);
// sum of the 10 recoded values * 2.5 → 0..100.
const SUS_POSITIVE = [
  "sus_frequent_use",
  "sus_easy_use",
  "sus_well_integrated",
  "sus_quick_learn",
  "sus_confident",
];
const SUS_NEGATIVE = [
  "sus_complex",
  "sus_need_support",
  "sus_inconsistent",
  "sus_cumbersome",
  "sus_need_learning",
];

export function computeSus(row) {
  if (!row) return null;
  let sum = 0;
  for (const key of SUS_POSITIVE) {
    const v = toNumber(row[key]);
    if (v === null) return null;
    sum += v - 1;
  }
  for (const key of SUS_NEGATIVE) {
    const v = toNumber(row[key]);
    if (v === null) return null;
    sum += 5 - v;
  }
  return sum * 2.5; // 0..100
}

// ---- Trust (Jian et al.), stored items 1..7 ----
// Negative (distrust) items are reverse-scored (8 - v); the trust index is the
// mean of all recoded items → 1..7 (higher = more trust).
// NOTE: a 12th item, `integrity`, is stored from migration 0007 onward
// (tpa_integrity). It is intentionally EXCLUDED from this composite so the
// trust index stays comparable across participants collected before the fix
// (whose tpa_integrity is NULL). Analyse integrity at the item level instead.
const TRUST_NEGATIVE = [
  "tpa_deceptive",
  "tpa_underhanded",
  "tpa_suspicious",
  "tpa_wary",
  "tpa_harmful",
];
const TRUST_POSITIVE = [
  "tpa_confident",
  "tpa_security",
  "tpa_dependable",
  "tpa_reliable",
  "tpa_trust",
  "tpa_familiar",
];

export function computeTrust(row) {
  if (!row) return null;
  const recoded = [];
  for (const key of TRUST_NEGATIVE) {
    const v = toNumber(row[key]);
    if (v === null) return null;
    recoded.push(8 - v);
  }
  for (const key of TRUST_POSITIVE) {
    const v = toNumber(row[key]);
    if (v === null) return null;
    recoded.push(v);
  }
  return mean(recoded); // 1..7
}

// ---- grouping ----

export function groupBy(items, keyFn) {
  const map = new Map();
  (items || []).forEach((item) => {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
}

function distinct(values) {
  return Array.from(new Set(values));
}

/**
 * Collapse duplicate caption ratings. A page refresh restarts the session, so a
 * participant can rate the same image twice; keeping both would double-count it
 * in the per-condition means. Keeps the FIRST rating per (participant, image).
 */
export function dedupCaptionRatings(afterImageRows) {
  const seen = new Set();
  return (afterImageRows || []).filter((row) => {
    const key = `${row?.participant_code}|${row?.imageurl}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Flag participants whose row counts deviate from a clean run, so duplicates
 * and dropouts are visible instead of silently averaged in.
 * Expected per completer: 1 starting, EXPECTED_IMAGES ratings, 3 post-phase, 1 ending.
 */
export function buildDataQuality(summary, expectedImages = 30) {
  const count = (rows, code) =>
    (rows || []).filter((r) => r?.participant_code === code).length;

  const codes = distinct(
    [
      ...(summary?.starting || []),
      ...(summary?.ending || []),
      ...(summary?.afterImage || []),
    ].map((r) => r?.participant_code)
  ).filter(Boolean);

  const issues = [];
  codes.forEach((code) => {
    const starting = count(summary?.starting, code);
    const rated = (summary?.afterImage || []).filter((r) => r?.participant_code === code);
    const images = rated.length;
    const distinctImages = distinct(rated.map((r) => r?.imageurl)).length;
    const phases = count(summary?.afterPhase, code);
    const ending = count(summary?.ending, code);
    const mode = rated[0]?.assigned_mode || (summary?.starting || []).find((r) => r?.participant_code === code)?.assigned_mode || "";

    const flags = [];
    if (starting > 1) flags.push(`${starting} starting rows (restarted)`);
    if (images > distinctImages) flags.push(`${images - distinctImages} duplicate rating(s)`);
    if (phases > 3) flags.push(`${phases} post-phase rows`);
    if (!ending) flags.push("no ending (incomplete)");
    else if (distinctImages < expectedImages) flags.push(`only ${distinctImages}/${expectedImages} images`);

    if (flags.length) {
      issues.push({
        participantCode: code,
        mode,
        label: conditionLabel(mode),
        starting,
        images,
        distinctImages,
        phases,
        ending,
        completed: ending > 0,
        flags: flags.join("; "),
      });
    }
  });

  return issues.sort((a, b) => Number(a.completed) - Number(b.completed));
}

/**
 * Build the per-condition overview from the /api/admin/summary payload.
 * Returns one row per condition plus a synthetic "All" total row is left to
 * the caller. Each row: counts, completion, and mean composites.
 */
export function buildConditionSummary(summary) {
  const assignments = summary?.assignments || [];
  const starting = summary?.starting || [];
  // Refresh-restarts can produce repeat ratings for the same image; collapse
  // them so per-condition caption means are not double-counted.
  const afterImage = dedupCaptionRatings(summary?.afterImage);
  const afterPhase = summary?.afterPhase || [];
  const ending = summary?.ending || [];

  const assignedByMode = groupBy(assignments, (r) => r.assigned_mode || "");
  const startingByMode = groupBy(starting, (r) => r.assigned_mode || "");
  const afterImageByMode = groupBy(afterImage, (r) => r.assigned_mode || "");
  const afterPhaseByMode = groupBy(afterPhase, (r) => r.assigned_mode || "");
  const endingByMode = groupBy(ending, (r) => r.assigned_mode || "");

  // Include any unexpected modes that appear in the data after the known five.
  const seenModes = distinct([
    ...CONDITIONS,
    ...assignments.map((r) => r.assigned_mode || ""),
    ...ending.map((r) => r.assigned_mode || ""),
  ]).filter((m) => m !== "");

  return seenModes.map((mode) => {
    const assignedCodes = distinct((assignedByMode.get(mode) || []).map((r) => r.participant_code));
    const startedCodes = distinct((startingByMode.get(mode) || []).map((r) => r.participant_code));
    const completedCodes = distinct((endingByMode.get(mode) || []).map((r) => r.participant_code));

    const captionRatings = (afterImageByMode.get(mode) || [])
      .map((r) => toNumber(r.caption_quality))
      .filter((v) => v !== null);
    const trustScores = (afterPhaseByMode.get(mode) || [])
      .map((r) => computeTrust(r))
      .filter((v) => v !== null);
    const susScores = (endingByMode.get(mode) || [])
      .map((r) => computeSus(r))
      .filter((v) => v !== null);

    return {
      mode,
      label: conditionLabel(mode),
      assigned: assignedCodes.length,
      started: startedCodes.length,
      completed: completedCodes.length,
      captionRatingCount: captionRatings.length,
      meanCaptionQuality: round(mean(captionRatings), 2),
      trustCount: trustScores.length,
      meanTrust: round(mean(trustScores), 2),
      susCount: susScores.length,
      meanSus: round(mean(susScores), 1),
    };
  });
}

/**
 * Per-condition interaction totals from the pre-aggregated activityStats.
 * Returns { modes: [...], byMode: { mode: { total, byTarget, totalDwellMs } } }.
 */
export function buildInteractionSummary(summary) {
  const stats = summary?.activityStats || [];
  const byMode = {};
  stats.forEach((row) => {
    const mode = row.assigned_mode || "(no condition)";
    if (!byMode[mode]) {
      byMode[mode] = { total: 0, totalDwellMs: 0, byTarget: {} };
    }
    const events = toNumber(row.events) || 0;
    const totalMs = toNumber(row.total_ms) || 0;
    const target = row.target_type || "(other)";
    byMode[mode].total += events;
    byMode[mode].totalDwellMs += totalMs;
    if (!byMode[mode].byTarget[target]) {
      byMode[mode].byTarget[target] = { events: 0, totalMs: 0 };
    }
    byMode[mode].byTarget[target].events += events;
    byMode[mode].byTarget[target].totalMs += totalMs;
  });
  return byMode;
}

// ---- CSV / download ----

function csvCell(value) {
  if (value === null || value === undefined) return "";
  const s = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Convert an array of row objects to CSV. Columns default to the union of all
 * keys (in first-seen order).
 */
export function toCsv(rows, columns) {
  const list = rows || [];
  let cols = columns;
  if (!cols) {
    const seen = [];
    const seenSet = new Set();
    list.forEach((row) => {
      Object.keys(row || {}).forEach((k) => {
        if (!seenSet.has(k)) {
          seenSet.add(k);
          seen.push(k);
        }
      });
    });
    cols = seen;
  }
  const header = cols.map(csvCell).join(",");
  const body = list.map((row) => cols.map((c) => csvCell(row?.[c])).join(",")).join("\n");
  return list.length ? `${header}\n${body}` : header;
}

export function downloadText(filename, text, mime) {
  const blob = new Blob([text], { type: mime || "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadJson(filename, value) {
  downloadText(filename, JSON.stringify(value, null, 2), "application/json");
}

export function downloadCsv(filename, rows, columns) {
  downloadText(filename, toCsv(rows, columns), "text/csv;charset=utf-8");
}

/** Timestamp-safe filename slug (no Date in tests — caller passes stamp). */
export function slugStamp(stamp) {
  return (stamp || "export").replace(/[^0-9A-Za-z_-]/g, "-");
}
